/* eslint-disable max-len */
/* eslint-disable dot-notation */

import Vue from 'vue';
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import {
  Attachment,
  AttachmentActionType,
  AttachmentInfo,
  AttachmentType,
  Lead, Task, TaskName, TaskStatus,
} from '@/types';

@Module
export default class Tasks extends VuexModule {
  task: Task | null = null
  loading = false
  updating = false

  // Definisce se il Task è stato modificato sul client
  // ed è necessario aggiornarlo.
  dirty = false

  // Definisce se il modulo utilizzato per raccoglie i campi
  // del Task è valido.
  valid = false

  // Definisce se contestualmente alla deselezione di un Task
  // va deselezionata anche la Lead corrispondente.
  unselectLeadOnClose = true

  // Nome del blocco DOM da mettere a fuoco all'apertura di EditTaskDialog
  // (corrisponde all'attributo `data-field` di un wrapper all'interno del Task).
  // Se null, il dialog si apre dal top come di default.
  focusField: string | null = null

  openTimestamp = 0

  // Contiene tutti i campi da inviare nel prossimo aggiornamento del Task
  update = {
  } as {
    [key: string]: never;
  }

  get selectedTask(): Task | null { return this.task; }
  get isTaskLoading(): boolean { return this.loading; }
  get isTaskUpdating(): boolean { return this.updating; }
  get isTaskDirty(): boolean { return this.dirty; }
  get isTaskValid(): boolean { return this.valid; }
  get selectedTaskUpdate(): { [key: string]: never } { return this.update; }
  get selectedTaskFocusField(): string | null { return this.focusField; }

  // Imposta il task selezionato
  @Mutation
  setTask({ task }: { task: Task | null }) {
    this.task = task;
  }

  @Mutation
  setTaskStatus({ status }: { status: TaskStatus }) {
    if (this.task) Vue.set(this.task, 'status', status);
  }

  // Imposta se sta caricando o meno il Task
  @Mutation
  setTaskLoading({ value }: { value: boolean }) { this.loading = value; }

  // Imposta se sta aggiornando o meno il Task
  @Mutation
  setTaskUpdating({ value }: { value: boolean }) { this.updating = value; }

  // Imposta se il Task è stato modificato sul client
  // ed è necessario aggiornarlo.
  @Mutation
  setTaskDirty({ value }: { value: boolean }) { this.dirty = value; }

  @Mutation
  setTaskValidity({ value }: { value: boolean }) { this.valid = value; }

  @Mutation
  setUnselectLeadOnClose({ value }: { value: boolean }) { this.unselectLeadOnClose = value; }

  @Mutation
  setFocusField({ value }: { value: string | null }) { this.focusField = value; }

  @Mutation
  addTaskAttachments({ attachments }: { attachments: Attachment[] }) {
    console.log('store/tasks - mutations:addTaskAttachments attachments =', attachments); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da aggiungere memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingAttachments: AttachmentInfo[] = this.update['attachments'] ?? [];
    console.log('store/tasks - mutations:addTaskAttachments existingAttachments =', existingAttachments); // eslint-disable-line no-console

    Vue.set(
      this.update,
      'attachments',
      [...existingAttachments, ...attachments],
    );
  }

  @Mutation
  deleteTaskAttachments({ id }: { id: number }) {
    console.log('store/tasks - mutations:deleteTaskAttachments id =', id); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da aggiungere memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingAttachments: AttachmentInfo[] = this.update['attachments'] ?? [];
    console.log('store/tasks - mutations:deleteTaskAttachments existingAttachments =', existingAttachments); // eslint-disable-line no-console

    // L'elenco degli `id` degli allegati da eliminare memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingDeletedAttachmentsIds: number[] = this.update['deletedAttachments'] ?? [];
    console.log('store/tasks - mutations:deleteTaskAttachments existingDeletedAttachmentsIds =', existingDeletedAttachmentsIds); // eslint-disable-line no-console

    // Rimuove l'allegato corrispondente tra quelli da aggiungere
    Vue.set(
      this.update,
      'attachments',
      existingAttachments.filter((aInfo) => aInfo.id !== id),
    );

    // Aggiunge l'id a quelli da eliminare
    Vue.set(
      this.update,
      'deletedAttachments',
      [...existingDeletedAttachmentsIds, id],
    );
  }

  @Mutation
  selectTaskAttachments({ attachments, type }: { attachments: Attachment[]; type: AttachmentType }) {
    console.log('store/tasks - mutations:selectTaskAttachments attachments =', attachments); // eslint-disable-line no-console
    console.log('store/tasks - mutations:selectTaskAttachments type =', type); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da duplicare memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingSelectedAttachments: AttachmentInfo[] = this.update['selectedAttachments'] ?? [];
    console.log('store/tasks - mutations:selectTaskAttachments existingSelectedAttachments =', existingSelectedAttachments); // eslint-disable-line no-console

    const selectedAttachments: AttachmentInfo[] = attachments.map((a) => ({ id: a.id, fieldname: type }));
    console.log('store/tasks - mutations:selectTaskAttachments selectedAttachments =', selectedAttachments); // eslint-disable-line no-console

    Vue.set(
      this.update,
      'selectedAttachments',
      [...existingSelectedAttachments, ...selectedAttachments],
    );
  }

  @Mutation
  unselectTaskAttachments({ id, type }: { id: number; type: AttachmentType }) {
    console.log('store/tasks - mutations:unselectTaskAttachments id =', id); // eslint-disable-line no-console
    console.log('store/tasks - mutations:unselectTaskAttachments type =', type); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da duplicare memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingSelectedAttachments: AttachmentInfo[] = this.update['selectedAttachments'] ?? [];
    console.log('store/tasks - mutations:unselectTaskAttachments existingSelectedAttachments =', existingSelectedAttachments); // eslint-disable-line no-console

    // Rimuove l'allegato corrispondente tra quelli da duplicare
    Vue.set(
      this.update,
      'selectedAttachments',
      existingSelectedAttachments.filter((aInfo) => aInfo.id !== id),
    );
  }

  // FIXME: Unificare updateTaskField() e updateConnectionField()
  @Mutation
  updateTaskField({ key, value }: { key: string; value: unknown}) {
    Vue.set(this.update, key, value);
  }

  @Mutation
  cleanUpdate() {
    this.update = {};
  }

  @Mutation
  setOpenTimestamp({ value }: { value: number }) { this.openTimestamp = value; }

  // Seleziona il task specificato
  @Action({ rawError: true })
  async selectTask(
    {
      name,
      leadId,
      fatherId = null,
      unselectLeadOnClose = true,
      focusField = null,
    }: {
      name: TaskName;
      leadId: number;
      fatherId: number | null;
      unselectLeadOnClose: boolean;
      focusField?: string | null;
    },
  ): Promise<Task | Error | null> {
    console.log(`store/tasks - actions:selectTask('${name}', ${leadId}, ${fatherId}, focusField=${focusField})`); // eslint-disable-line no-console

    if (!leadId) return Promise.reject(new Error('leadId non specificato'));

    // Memorizza il blocco da mettere a fuoco all'apertura del dialog
    this.context.commit('setFocusField', { value: focusField });

    // Avvia gli eventuali indicatori di caricamento del Task
    this.context.commit('setTaskLoading', { value: true });

    // Seleziona la Lead a cui appartiene il Task
    const lead: Lead | Error = await this.context.dispatch('selectLead', { id: leadId, noChildren: true });

    // Recupera il Task dalla Lead selezionata
    const task = lead && !(lead instanceof Error)
      ? lead?.Tasks.find((t) => t.name === name) ?? null
      : null;

    console.log('store/tasks - actions:selectTask task =', task); // eslint-disable-line no-console

    if (!task) {
      this.context.commit('setTaskLoading', { value: false });
      this.context.dispatch('unselectTask');

      return Promise.reject();
    }

    // Se richiesto, recupera anche il padre (o lo aggiorna)
    try {
      if (fatherId) await this.context.dispatch('updateProject', { id: fatherId, noChildren: true });
    } catch (error) {
      console.error('store/tasks - actions:selectTask - error =', error); // eslint-disable-line no-console
    }

    // Memorizza il valore di unselectLeadOnClose.
    // Verrà utilizzato da unselectTask per decidere se
    // deselezionare anche la Lead.
    this.context.commit('setUnselectLeadOnClose', { value: unselectLeadOnClose });

    // Memorizza il Task selezionato
    this.context.commit('setTask', { task });

    // Reset della validità del form del Task
    this.context.commit('setTaskValidity', { value: false });

    // Il Task è appena stato recuperato e non va aggiornato
    this.context.commit('setTaskDirty', { value: false });

    // Ferma gli eventuali indicatori di caricamento del Task
    this.context.commit('setTaskLoading', { value: false });

    // Ritorna il Task selezionato
    return Promise.resolve(task);
  }

  // Deseleziona il task selezionato precedentemente
  @Action({ rawError: true })
  async unselectTask() {
    console.log('store/tasks - actions:unselectTask'); // eslint-disable-line no-console

    // Svuota l'elenco dei campi da aggiornare
    this.context.commit('cleanUpdate');

    if (this.unselectLeadOnClose === true) {
      this.context.dispatch('unselectLead');
    }

    this.context.commit('setTask', { task: null });

    this.context.commit('setTaskUpdating', { value: false });
    this.context.commit('setTaskLoading', { value: false });
    this.context.commit('setTaskDirty', { value: false });
    this.context.commit('setFocusField', { value: null });

    return true;
  }

  @Action({ rawError: true })
  updateTaskFields(fields: { [key: string]: unknown }) {
    console.log('store/tasks - actions:updateTaskFields - fields =', fields); // eslint-disable-line no-console

    Object.entries(fields).forEach(([key, value]) => {
      // Imposta direttamente tutti i campi (eccetto gli allegati)
      this.context.commit('updateTaskField', { key, value });
    });

    // Ci sono campi del Task da aggiornare
    this.context.commit('setTaskDirty', { value: true });
  }

  @Action({ rawError: true })
  async updateSelectedTask() {
    console.log('store/tasks - actions:updateSelectedTask'); // eslint-disable-line no-console

    const saveTimestamp = Date.now();

    console.log('store/tasks - actions:updateSelectedTask: saveDialogTimestamp =', saveTimestamp); // eslint-disable-line no-console

    // Avvia gli eventuali indicatori di aggiornamento del Task
    this.context.commit('setTaskUpdating', { value: true });

    // Trasmetto soltanto gli id degli attachments
    const { attachments = [], ...others } = this.update;

    // Richiede l'aggiornamento del Task associato alla Lead
    await this.context.dispatch('updateLead', {
      updateType: 'task',
      update: {
        ...(
          attachments.length > 0
            ? { attachments: (attachments as { id: number; fieldname: string }[]).map((a) => a.id) }
            : {}
        ),
        ...others,
        task: this.task,
        openTimestamp: this.openTimestamp,
        saveTimestamp,
      },
    });

    // Se l'update contiene anche un aggiornamento di stato...
    if ('status' in this.update) {
      // ... aggiorna lo stato del Task selezionato
      this.context.commit('setTaskStatus', { status: this.update.status });
    }

    // Svuota l'elenco dei campi da aggiornare
    setTimeout(() => {
      this.context.commit('cleanUpdate');
    }, 2000);

    // Il Task è appena stato aggiornato
    this.context.commit('setTaskDirty', { value: false });

    // Ferma gli eventuali indicatori di aggiornamento del Task
    this.context.commit('setTaskUpdating', { value: false });
  }

  // FIXME: Unificare taskAttachments() e connectionAttachments()
  @Action({ rawError: true })
  async taskAttachments({
    action,
    attachmentId,
    attachments,
    type,
  }: {
    action: AttachmentActionType;
    attachmentId?: number;
    attachments?: Attachment[];
    type?: AttachmentType;
  }) {
    console.log('store/tasks - actions:taskAttachments() - action =', action); // eslint-disable-line no-console

    switch (action) {
      case 'add':
        this.context.commit('addTaskAttachments', { attachments });
        break;

      case 'delete':
        this.context.commit('deleteTaskAttachments', { id: attachmentId });
        break;

      case 'select':
        this.context.commit('selectTaskAttachments', { attachments, type });
        break;

      case 'unselect':
        this.context.commit('unselectTaskAttachments', { id: attachmentId, type });
        break;

      default:
        break;
    }

    // Ci sono campi della Connection da aggiornare
    this.context.commit('setTaskDirty', { value: true });

    return Promise.resolve();
  }

  @Action({ rawError: true })
  updateTaskFieldsValidity({ value }: { value: boolean }) {
    console.log('store/tasks - actions:updateTaskFieldsValidity'); // eslint-disable-line no-console

    this.context.commit('setTaskValidity', { value });
  }

  // Aggiorna lo stato di un Task specificato
  @Action({ rawError: true })
  async updateTaskStatus({ task, status }: { task: Task; status: TaskStatus }) {
    console.log('store/tasks - actions:updateTaskStatus'); // eslint-disable-line no-console

    // Avvia gli eventuali indicatori di aggiornamento del Task
    this.context.commit('setTaskUpdating', { value: true });

    // Richiede l'aggiornamento del Task associato alla Lead
    await this.context.dispatch('updateLead', {
      updateType: 'task',
      update: { status, task },
    });

    // Ferma gli eventuali indicatori di aggiornamento del Task
    this.context.commit('setTaskUpdating', { value: false });
  }

  @Action({ rawError: true })
  openDialogTimestamp() {
    console.log('store/tasks - actions:openDialogTimestamp'); // eslint-disable-line no-console

    const openDialogTimestamp = Date.now();

    this.context.commit('setOpenTimestamp', { value: openDialogTimestamp });

    console.log('store/tasks - actions:openDialogTimestamp openDialogTimestamp=', openDialogTimestamp); // eslint-disable-line no-console
  }
}
